/**
 * FAQ 목록 페이지
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import DataTable, { Column } from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import FilterBar, { FilterField } from '../../components/common/FilterBar';
import { faqService } from '../../services/faqService';
import { FAQ, FAQFilter } from '../../types/faq';

const FAQsList: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FAQFilter>({
    page: 1,
    limit: 20,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['faqs', filter],
    queryFn: () => faqService.getFAQs(filter),
  });

  const getStatusBadge = (status: string) => {
    return status === 'active' ? (
      <Badge bg="success">활성</Badge>
    ) : (
      <Badge bg="secondary">비활성</Badge>
    );
  };

  const columns: Column<FAQ>[] = [
    {
      key: 'id',
      label: 'ID',
      width: '80px',
    },
    {
      key: 'display_order',
      label: '순서',
      width: '80px',
      render: (faq) => (
        <span style={{ color: '#00BCD4', fontWeight: 'bold' }}>
          {faq.display_order}
        </span>
      ),
    },
    {
      key: 'category',
      label: '카테고리',
      render: (faq) => (
        <span style={{ color: '#ccc' }}>{faq.category || '-'}</span>
      ),
    },
    {
      key: 'question',
      label: '질문',
      render: (faq) => (
        <span
          style={{ cursor: 'pointer', color: '#00BCD4' }}
          onClick={() => navigate(`/admin/faqs/${faq.id}`)}
        >
          {faq.question.ko || faq.question.en || '-'}
        </span>
      ),
    },
    {
      key: 'status',
      label: '상태',
      render: (faq) => getStatusBadge(faq.status),
    },
    {
      key: 'created_at',
      label: '등록일',
      render: (faq) => (
        <span style={{ color: '#ccc' }}>
          {new Date(faq.created_at).toLocaleDateString('ko-KR')}
        </span>
      ),
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: 'search',
      label: '검색',
      type: 'text',
      placeholder: '질문, 답변으로 검색',
    },
    {
      name: 'category',
      label: '카테고리',
      type: 'text',
      placeholder: '카테고리로 검색',
    },
    {
      name: 'status',
      label: '상태',
      type: 'select',
      options: [
        { value: '', label: '전체' },
        { value: 'active', label: '활성' },
        { value: 'inactive', label: '비활성' },
      ],
    },
  ];

  const handleFilterChange = (values: Record<string, any>) => {
    setFilter((prev) => ({
      ...prev,
      ...values,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const handleRowClick = (faq: FAQ) => {
    navigate(`/admin/faqs/${faq.id}`);
  };

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>FAQ 관리</h1>
          <Button variant="primary" onClick={() => navigate('/admin/faqs/new')}>
            FAQ 등록
          </Button>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <FilterBar
              fields={filterFields}
              values={filter}
              onChange={handleFilterChange}
              onSearch={() => refetch()}
              onReset={() => setFilter({ page: 1, limit: 20 })}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                총 <strong>{data?.pagination.total_count || 0}</strong>건
              </div>
            </div>

            <DataTable
              columns={columns}
              data={data?.faqs || []}
              loading={isLoading}
              emptyMessage="FAQ가 없습니다."
              onRowClick={handleRowClick}
            />

            {data && data.pagination.total_pages > 1 && (
              <Pagination
                currentPage={data.pagination.current_page}
                totalPages={data.pagination.total_pages}
                onPageChange={handlePageChange}
              />
            )}
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default FAQsList;

